import { useEffect, useMemo, useState } from "react";
import { adminCleanupScan, adminCleanupDelete } from "@/lib/api";
import { Loader2, AlertTriangle, Trash2, RefreshCw, ShieldCheck, Copy, FlaskConical, Users } from "lucide-react";
import { toast } from "sonner";

const COLL_LABEL = {
  content: "Content (Kathas, Mantras, Stotras, Pooja, Suktas…)",
  hub: "Knowledge Hub",
  temples: "Temples",
  deities: "Deities",
  glossary: "Glossary",
  services: "Services",
  posts: "Patrika (Blog)",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AdminCleanup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(() => new Set());      // keys `${coll}:${id}`
  const [userSel, setUserSel] = useState(() => new Set());
  const [clearActivity, setClearActivity] = useState(false);
  const [wipe, setWipe] = useState(() => new Set()); // whole-collection wipes
  const [seededOnly, setSeededOnly] = useState(true); // wipe only original hardcoded pages
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const scan = () => {
    setLoading(true); setErr("");
    adminCleanupScan().then((d) => {
      setData(d);
      // Pre-select the non-recommended copies in each duplicate group.
      const pre = new Set();
      Object.entries(d.collections || {}).forEach(([coll, c]) => {
        [...(c.duplicate_slugs || []), ...(c.duplicate_titles || [])].forEach((g) => {
          g.items.forEach((it) => { if (it.id !== g.keep_id) pre.add(`${coll}:${it.id}`); });
        });
      });
      setSel(pre);
    }).catch((e) => setErr(e.response?.data?.detail || e.message)).finally(() => setLoading(false));
  };
  useEffect(scan, []);

  const toggle = (key) => setSel((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleUser = (id) => setUserSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleWipe = (coll) => setWipe((s) => { const n = new Set(s); n.has(coll) ? n.delete(coll) : n.add(coll); return n; });

  const wipeTotal = useMemo(() => [...wipe].reduce((a, coll) => {
    const c = data?.collections?.[coll];
    return a + (c ? (seededOnly ? c.hardcoded : c.total) : 0);
  }, 0), [wipe, data, seededOnly]);
  const totalSelected = sel.size + userSel.size + wipeTotal + (clearActivity ? (data?.activity_count || 0) : 0);

  const doDelete = async () => {
    if (confirm.trim().toUpperCase() !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
    const payload = { clear_activity: clearActivity, user_ids: [...userSel], wipe_collections: [...wipe], seeded_only: seededOnly };
    Object.keys(COLL_LABEL).forEach((coll) => { payload[`${coll}_ids`] = []; });
    sel.forEach((k) => { const [coll, id] = k.split(":"); payload[`${coll}_ids`].push(id); });
    setBusy(true);
    try {
      const res = await adminCleanupDelete(payload);
      const n = Object.values(res.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${n} record${n === 1 ? "" : "s"}`);
      setConfirm(""); setClearActivity(false); setUserSel(new Set()); setWipe(new Set());
      scan();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const Row = ({ coll, it, isKeep }) => {
    const key = `${coll}:${it.id}`;
    return (
      <label className={`flex items-center gap-3 px-3 py-2 text-sm ${isKeep ? "bg-emerald-50/60" : ""}`} data-testid={`cleanup-row-${it.id}`}>
        <input type="checkbox" checked={sel.has(key)} onChange={() => toggle(key)} className="h-4 w-4 accent-[var(--saffron)]" data-testid={`cleanup-check-${it.id}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-ink">{it.title} {isKeep && <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-700">Recommended keep</span>}</p>
          <p className="text-xs text-ink-3">/{it.slug} · {it.type || "—"} · {it.status}{it.hidden ? " · hidden" : ""} · body:{it.has_body ? "✓" : "✕"} img:{it.has_image ? "✓" : "✕"} · updated {fmtDate(it.updated_at)}</p>
        </div>
      </label>
    );
  };

  const Group = ({ coll, g, icon }) => (
    <div className="border border-line" data-testid="cleanup-group">
      <div className="flex items-center gap-2 border-b border-line bg-parchment/50 px-3 py-1.5 text-xs text-ink-2">
        {icon} <span className="font-medium">{g.key}</span> <span className="text-ink-3">· {g.items.length} copies</span>
      </div>
      <div className="divide-y divide-line">
        {g.items.map((it) => <Row key={it.id} coll={coll} it={it} isKeep={it.id === g.keep_id} />)}
      </div>
    </div>
  );

  if (loading) return <p className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Scanning the database…</p>;
  if (err) return <p className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">Scan failed: {err}</p>;

  const colls = data.collections || {};
  const anyDup = Object.values(colls).some((c) => c.duplicate_slugs.length || c.duplicate_titles.length);
  const anyTest = Object.values(colls).some((c) => c.test.length) || (data.users.test.length > 0);

  return (
    <div data-testid="admin-cleanup-page" className="pb-32">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Data Sanitize</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-3">Review duplicates and leftover test data, then delete exactly what you choose. Nothing is removed until you confirm. Admin accounts are protected and can never be deleted here.</p>
        </div>
        <button onClick={scan} className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron" data-testid="cleanup-rescan"><RefreshCw className="h-4 w-4" /> Re-scan</button>
      </div>

      <div className="mb-6 flex items-start gap-2 border border-[var(--gold)]/40 bg-[var(--surface-alt)]/50 px-4 py-3 text-sm text-ink-2">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
        In each duplicate group the richest / most recently updated copy is marked <span className="font-medium text-emerald-700">Recommended keep</span> and left unchecked; the others are pre-selected for deletion. Uncheck anything you want to keep.
      </div>

      {/* Duplicates */}
      <section className="mb-10">
        <h2 className="mb-3 flex items-center gap-2 font-serif-latin text-lg text-ink"><Copy className="h-4 w-4 text-saffron" /> Duplicates</h2>
        {!anyDup && <p className="border border-line bg-surface px-4 py-6 text-center text-sm text-ink-3" data-testid="cleanup-no-dup">No duplicate entries found. 🎉</p>}
        {Object.entries(colls).map(([coll, c]) => (
          (c.duplicate_slugs.length > 0 || c.duplicate_titles.length > 0) && (
            <div key={coll} className="mb-6">
              <p className="mb-2 text-sm font-medium text-ink-2">{COLL_LABEL[coll] || coll}</p>
              <div className="space-y-3">
                {c.duplicate_slugs.map((g) => <Group key={`s-${g.key}`} coll={coll} g={g} icon={<span className="text-[10px] uppercase text-ink-3">same address</span>} />)}
                {c.duplicate_titles.map((g) => <Group key={`t-${g.key}`} coll={coll} g={g} icon={<span className="text-[10px] uppercase text-ink-3">same title</span>} />)}
              </div>
            </div>
          )
        ))}
      </section>

      {/* Test / junk data */}
      <section className="mb-10">
        <h2 className="mb-3 flex items-center gap-2 font-serif-latin text-lg text-ink"><FlaskConical className="h-4 w-4 text-saffron" /> Test / placeholder data</h2>
        {!anyTest && <p className="border border-line bg-surface px-4 py-6 text-center text-sm text-ink-3" data-testid="cleanup-no-test">No obvious test or placeholder records found.</p>}
        {Object.entries(colls).map(([coll, c]) => (
          c.test.length > 0 && (
            <div key={coll} className="mb-5">
              <p className="mb-2 text-sm font-medium text-ink-2">{COLL_LABEL[coll] || coll} <span className="text-ink-3">({c.test.length})</span></p>
              <div className="border border-line divide-y divide-line">
                {c.test.map((it) => <Row key={it.id} coll={coll} it={it} isKeep={false} />)}
              </div>
            </div>
          )
        ))}
      </section>

      {/* Test users */}
      <section className="mb-10">
        <h2 className="mb-3 flex items-center gap-2 font-serif-latin text-lg text-ink"><Users className="h-4 w-4 text-saffron" /> Test users <span className="text-sm font-normal text-ink-3">({data.users.test.length} of {data.users.total} total · admins hidden)</span></h2>
        {data.users.test.length === 0 ? (
          <p className="border border-line bg-surface px-4 py-6 text-center text-sm text-ink-3">No test-looking user accounts found.</p>
        ) : (
          <div className="border border-line divide-y divide-line">
            {data.users.test.map((u) => (
              <label key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`cleanup-user-${u.id}`}>
                <input type="checkbox" checked={userSel.has(u.id)} onChange={() => toggleUser(u.id)} className="h-4 w-4 accent-[var(--saffron)]" />
                <div className="min-w-0 flex-1"><p className="truncate text-ink">{u.name || "(no name)"} · <span className="text-ink-3">{u.email}</span></p>
                <p className="text-xs text-ink-3">{u.role} · joined {fmtDate(u.created_at)}</p></div>
              </label>
            ))}
          </div>
        )}
      </section>

      {/* Danger zone — wipe entire sections (seeded / hardcoded pages) */}
      <section className="mb-10">
        <h2 className="mb-1 flex items-center gap-2 font-serif-latin text-lg text-red-700"><AlertTriangle className="h-4 w-4" /> Danger zone — delete entire sections</h2>
        <p className="mb-3 max-w-2xl text-sm text-ink-3">Removes the pages in the ticked sections. Deleted items go to <span className="font-medium text-ink-2">Trash</span> and can be restored for 30 days.</p>
        <label className="mb-3 flex items-center gap-3 border border-line bg-parchment/40 px-4 py-2.5 text-sm text-ink-2" data-testid="cleanup-seeded-only">
          <input type="checkbox" checked={seededOnly} onChange={(e) => setSeededOnly(e.target.checked)} className="h-4 w-4 accent-[var(--saffron)]" />
          Only delete the original seeded / hardcoded pages — <span className="font-medium text-ink">keep anything I created in the admin</span>
        </label>
        <div className="border border-red-200">
          {Object.entries(colls).map(([coll, c]) => {
            const willDelete = seededOnly ? c.hardcoded : c.total;
            return (
              <label key={coll} className={`flex items-center gap-3 border-b border-red-100 px-4 py-2.5 text-sm last:border-0 ${wipe.has(coll) ? "bg-red-50" : ""}`} data-testid={`cleanup-wipe-${coll}`}>
                <input type="checkbox" checked={wipe.has(coll)} onChange={() => toggleWipe(coll)} className="h-4 w-4 accent-red-600" />
                <span className="flex-1 text-ink">{COLL_LABEL[coll] || coll}</span>
                <span className="text-xs text-ink-3">{c.total} page{c.total === 1 ? "" : "s"}{c.authored > 0 ? ` · ${c.hardcoded} original, ${c.authored} yours` : ""}</span>
                {wipe.has(coll) && <span className="text-xs font-medium text-red-700">{willDelete} will be deleted</span>}
              </label>
            );
          })}
        </div>
      </section>

      {/* Activity log */}
      <section className="mb-10">
        <h2 className="mb-3 font-serif-latin text-lg text-ink">Activity log</h2>
        <label className="flex items-center gap-3 border border-line bg-surface px-4 py-3 text-sm text-ink-2" data-testid="cleanup-clear-activity">
          <input type="checkbox" checked={clearActivity} onChange={(e) => setClearActivity(e.target.checked)} className="h-4 w-4 accent-[var(--saffron)]" />
          Clear the entire activity log ({data.activity_count} entries)
        </label>
      </section>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-parchment/95 backdrop-blur md:pl-64">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-5 py-3">
          <span className="inline-flex items-center gap-1.5 text-sm text-ink-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> <span className="font-medium text-ink">{totalSelected}</span> record{totalSelected === 1 ? "" : "s"} selected for deletion</span>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Type DELETE to confirm' data-testid="cleanup-confirm-input"
            className="ml-auto w-48 border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-red-500 focus:outline-none" />
          <button onClick={doDelete} disabled={busy || totalSelected === 0 || confirm.trim().toUpperCase() !== "DELETE"} data-testid="cleanup-delete-btn"
            className="inline-flex items-center gap-2 bg-red-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete selected
          </button>
        </div>
      </div>
    </div>
  );
}
